import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import {
	ModalBody,
	ModalFooter,
	Button,
	Autocomplete,
	AutocompleteItem,
	AutocompleteSection,
	Select,
	SelectItem,
	Input,
	Textarea,
	Checkbox,
	Chip,
	Tabs,
	Tab,
	RadioGroup,
	Radio,
	addToast
} from '@heroui/react';
import { Plane, Wrench } from 'lucide-react';
import { fromDate, CalendarDate, getDayOfWeek } from '@internationalized/date';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import CalendarApi from '../../../api/CalendarApi.js';
import { AppDatePicker } from '../../resources/components/AppDatePicker.js';
import {
	pickCalendarBootstrap,
	pickCalendarFilters,
	pickCalendarRefs
} from '../../calendar-common/viewStoreKeys.js';
import { useCalendarPermissions } from '../hooks/useCalendarPermissions.js';
import { getEventOrgPerms } from '../utils/getEventOrgPerms.js';
import { selectEventDefaultLengthSec } from '../selectors/calendarMonthSelectors.js';
import { buildEventPayload } from '../utils/buildEventPayload.js';
import {
	computeEffectiveAddToWaiting,
	resolveSubmitLabel,
	scanResourcesAddToWaiting
} from '../utils/addToWaiting.js';
import { cdtToJSDate, cdtToEpoch, epochToCalendarDateTime } from '../utils/dateTime.js';
import { formatRecurrenceSummary } from '../utils/recurrenceSummary.js';
import { sortResourceGroupsByPriority } from '../utils/sortResourceGroupsByPriority.js';
import { buildResourceBlockerMaps } from '../utils/resourceBlockers.js';
import {
	extractPrecheckErrors,
	extractInstructorsBlocker,
	dropResourceBlockerTwins,
	blockingPrecheckErrors
} from '../utils/precheckErrors.js';
import { LocationLabel, formatLocationLabelString } from '../utils/formatLocationLabel.js';
import { EDIT_RECURRING_OPTIONS } from './RecurringScopeConfirm.js';

// ─── Assigned-person candidate seeding (HW-1022 Task:20) ─────────────────────
// The Pilot/SIC/Instructor dropdown ITEMS are driven by `candidates.*`, which
// `/calendar/check-event-params` populates async (and may OMIT the assigned
// users for a checked-out/completed event). Without a matching item the
// assigned (or auto-filled author) chip can't render → the field looks empty.
// These pure helpers seed `candidates` from the refs maps (`state.pilots` /
// `state.instructors`, available at open) so the chip renders immediately;
// the precheck response is then MERGED in (deduped by id) without dropping the
// assigned seed.
const mapValues = map => (map ? (Array.isArray(map) ? map : Object.values(map)) : []);

/** Pilot/SIC candidate item from the pilots refs map, matched by USER id. */
function pilotCandidateFromRefs(pilotsMap, userId) {
	if (userId == null || userId === '') return null;
	const x = mapValues(pilotsMap).find(
		p => p && String(p.id ?? p.pilotID ?? p.userID ?? '') === String(userId)
	);
	if (!x) return null;
	return {
		id: String(userId),
		name: x.pilotName ?? x.name ?? '',
		surname: x.pilotSurname ?? x.surname ?? ''
	};
}

/**
 * Instructor candidate item from the instructors refs map, matched by RESOURCE
 * id (the `instructorId` form value is a resource id on edit). The refs DTO has
 * no underlying user id, so `instructorId` (user id, used by the mutual-
 * exclusion disable) is left undefined here — the precheck merge supplies the
 * richer item with `instructorId` when it arrives.
 */
function instructorCandidateFromRefs(instructorsMap, resourceId) {
	if (resourceId == null || resourceId === '') return null;
	const x = mapValues(instructorsMap).find(
		i => i && String(i.id ?? i.resourceID ?? '') === String(resourceId)
	);
	if (!x) return null;
	return {
		id: String(resourceId),
		name: x.name ?? x.instructorName ?? '',
		surname: x.instructorSurname ?? ''
	};
}

/** Seed `candidates` from the currently-assigned ids so chips render at open. */
function buildAssignedCandidates(pilotsMap, instructorsMap, ids = {}) {
	const pilot = pilotCandidateFromRefs(pilotsMap, ids.pilotId);
	const sic = pilotCandidateFromRefs(pilotsMap, ids.sicPilotId);
	const instructor = instructorCandidateFromRefs(instructorsMap, ids.instructorId);
	return {
		pilots: pilot ? [pilot] : [],
		sics: sic ? [sic] : [],
		instructors: instructor ? [instructor] : []
	};
}

/**
 * Merge a precheck-response candidate list with the seeded assigned item:
 * the response wins, but the currently-assigned item is PRESERVED (deduped by
 * id) when the response omits it — e.g. a checked-out event whose assigned
 * pilot is no longer reassignable. Never produces a duplicate.
 */
function mergeKeepAssigned(incoming, prevList, assignedId) {
	const list = Array.isArray(incoming) ? incoming : [];
	if (!assignedId) return list;
	if (list.some(it => String(it.id) === String(assignedId))) return list;
	const kept = mapValues(prevList).find(it => String(it.id) === String(assignedId));
	return kept ? [...list, kept] : list;
}

// Sunday=0..Saturday=6 to match legacy `daysOfWeek` string keys and the
// backend DTO contract (`EventParamsRequestDto.php:29-53`).
const WEEK_DAYS = [
	{ key: '0', short: 'Sun', full: 'Sunday' },
	{ key: '1', short: 'Mon', full: 'Monday' },
	{ key: '2', short: 'Tue', full: 'Tuesday' },
	{ key: '3', short: 'Wed', full: 'Wednesday' },
	{ key: '4', short: 'Thu', full: 'Thursday' },
	{ key: '5', short: 'Fri', full: 'Friday' },
	{ key: '6', short: 'Sat', full: 'Saturday' }
];

/** Field — shared label/value wrapper matching EventViewModal typography. */
// Stable empty-map fallback for the activity-types selector — avoids a fresh
// `{}` per render that would defeat `useSelector` reference equality.
const EMPTY_ACTIVITY_TYPES = {};

function Field({ label, required = false, children, hint = null, isInvalid = false }) {
	return (
		<div className="mb-3">
			<p
				className={`text-xs font-semibold uppercase tracking-wide mb-0.5 ${isInvalid ? 'text-danger' : 'text-default-500'}`}
			>
				{label}
				{required && (
					<span className="text-danger ml-0.5" aria-hidden="true">
						*
					</span>
				)}
			</p>
			<div className="text-sm text-default-900">{children}</div>
			{hint && <p className="text-xs text-danger mt-1.5">{hint}</p>}
		</div>
	);
}

/**
 * EventFormBody — reusable Event form body+footer for both Create and Edit.
 *
 * Used by:
 *   - `CreateEventModal` (mode='create')   — fresh form, defaults from page org
 *   - `EventViewModal`   (mode='edit')     — hydrated from `mapEventToFormState`
 *
 * The parent (CreateEventModal / EventViewModal) provides the `<Modal>` +
 * `<ModalContent>` + `<ModalHeader>` chrome. This component renders only the
 * `<ModalBody>` + `<ModalFooter>` slice — the form itself plus its action
 * buttons. Form state is local to this component (so a single shared form
 * never has stale state from a previous session).
 *
 * Submit flow:
 *   1. Validate required fields locally (toast on miss).
 *   2. Build the wire payload via `buildEventPayload` (single source of truth
 *      shared with the debounced pre-check).
 *   3. Call `onSave(payload, formOrgUnique)`. Parent decides which endpoint to
 *      hit (createEvent / createMyEvent / updateEvent / updateMyEvent) and
 *      what to do on success (toast + close + refetch).
 *
 * @param {Object}   props
 * @param {'create'|'edit'} props.mode
 * @param {boolean}  props.isOpen                 Forwarded so init effects fire only when shown.
 * @param {string}   props.orgUnique              Page-level org slug (used for create init).
 * @param {number|null} [props.initialDayTs]      Create-only: epoch seconds for the clicked day.
 * @param {number|string} [props.eventId]         Edit-only: event id for skip-self pre-check.
 * @param {string}   [props.eventOrgUnique]       Edit-only: event's owning org for cross-org URL.
 * @param {Object}   [props.initialFormState]     Edit-only: hydrated form state from `mapEventToFormState`.
 * @param {boolean}  [props.recurringEditEnabled] Edit-only flag — true when scope dialog has run.
 *                                                 (Reserved; current edit flow opens the dialog
 *                                                  inside the parent BEFORE calling onSave.)
 * @param {(payload: Object, formOrgUnique: string) => Promise<{ ok: boolean }>} props.onSave
 * @param {() => void} props.onCancel
 * @param {string}   [props.submitLabel]          'Create' | 'Save' (default by mode).
 */
function EventFormBody({
	mode = 'create',
	isOpen,
	orgUnique,
	initialDayTs,
	initialStartTs,
	eventId,
	eventOrgUnique,
	initialFormState,
	onSave,
	onCancel,
	submitLabel,
	// Cross-org override — when editing an event from a non-current org, the
	// caller (EventViewModal) computes the event-org `canManageRecurrent` from
	// `eventOrgPerms[eventOrgUnique]` and passes it here so the recurrence
	// gate (UI section, willRecur payload, defensive reset) reflects the
	// EVENT's org, not the page's org. `undefined` (the default) preserves
	// create-mode behavior — falls back to current-page perms.
	effectiveCanManageRecurrent,
	// Optional: called with (resourceId, label) when user clicks a resource chip body.
	// If omitted, chips are non-interactive (close-only).
	onResourceClick,
	// Waiting List flow — create-mode hydration may prefill from
	// `initialFormState` (e.g. cloning an existing event into a waiting-list
	// entry). When true, `buildEventPayload` emits `addToWaiting: true`.
	forceAddToWaiting = false,
	// Optional: called with the new event type string whenever eventType changes
	// (including on modal open/reset). Lets parent components (e.g. CreateEventModal)
	// reflect the selected type in the header without lifting state.
	onEventTypeChange
}) {
	const isEditMode = mode === 'edit';
	const onClose = onCancel; // legacy alias used by inner JSX
	const resourcesMap = useSelector(state => state?.resources);
	const organizationsMap = useSelector(state => state?.organizations);
	const locationsMap = useSelector(state => state?.locations);
	const pilotsMap = useSelector(state => state?.pilots);
	// HW-1081: is `userId` a pilot of `org`? Reads the refs map directly. The
	// `pilots` memo is scoped to `formOrgUnique` from the PREVIOUS render, which at
	// open time can still be another org (the modal never unmounts) or '' (no
	// filter), and `/calendar/pilots` lists the pilots of every org the user is in.
	const isPilotInOrg = (userId, org) =>
		userId != null &&
		mapValues(pilotsMap).some(
			x =>
				x &&
				!x.trash &&
				(!org || x.organizationUnique === org) &&
				String(x.id ?? x.userID ?? x._userID ?? '') === String(userId)
		);
	const instructorsMap = useSelector(state => state?.instructors);
	const customFieldsMap = useSelector(state => state?.customFields);
	// Rem32 Task 1 — activity-types map (Rem30 Sub 5; org-scoped at fetch time,
	// so it already contains only the dashboard org's types). Drives the
	// optional Activity Type selector below.
	const activityTypesMap = useSelector(
		state => pickCalendarRefs(state)?.activityTypes || EMPTY_ACTIVITY_TYPES
	);
	// Rem32 Task 2 — org-level "event sharing" toggle. Org-settings flags
	// serialize as the string `'1'` / `'0'` (same shape `CalendarMonthNav`
	// reads). The calendar is locked to the dashboard org (Rem30 Task 3), so
	// `calendarMonthBootstrap.organizationSettings` IS the form org's settings.
	const enableEventSharing = useSelector(
		state => String(pickCalendarBootstrap(state)?.organizationSettings?.enableEventSharing) === '1'
	);
	// Symfony `OrganizationSettingName::ENFORCE_COMMENTS` is type `bool`
	// (`OrganizationSettingName.php:58`), so `Organization::mapSettingsToUniqueValues`
	// casts it to a real boolean — the dashboard bootstrap delivers `true`/`false`,
	// NOT the string `'1'`. `String(true) === '1'` is false, which wrongly read
	// enforce-comments as OFF. Detect truthy across every shape (bool/number/string).
	const enforseComments = useSelector(state => {
		const v = pickCalendarBootstrap(state)?.organizationSettings?.enforseComments;
		return v === true || v === 1 || v === '1' || v === 'true';
	});
	// Org setting `pilotOptional` — legacy reads `=== "1"` (string), but the
	// dashboard bootstrap may cast bool settings; detect truthy across shapes
	// (mirror `enforseComments`). Gates the conditional Pilot-required rule.
	const pilotOptional = useSelector(state => {
		const v = pickCalendarBootstrap(state)?.organizationSettings?.pilotOptional;
		return v === true || v === 1 || v === '1' || v === 'true';
	});
	const currentUserId = useSelector(
		state => state?.user?.id ?? state?.user?.userId ?? state?.user?._userID ?? null
	);
	const timezone = useSelector(
		state => pickCalendarFilters(state)?.timezone || pickCalendarBootstrap(state)?.timezone || 'UTC'
	);
	// `effectivePerm` resolves the event-relevant `canManageRecurrent`:
	// the caller's prop wins (cross-org edit), otherwise the current page's
	// `useCalendarPermissions()` (create flow).
	// `canManageRecurrent` mirrors legacy `calendar.event-add.js:812-846`
	// `ng-if="accessgroups.calendarManageRecurrentEvent"`. Gates both UI
	// (checkbox + inline expansion) and the payload (`willRecur` short-
	// circuits → neutral recurrence fields).
	const { canManageRecurrent: pageCanManageRecurrent } = useCalendarPermissions();
	// Tri-state resolution:
	//   prop === true  → has permission (from event's org)
	//   prop === false → resolved, no permission
	//   prop === null  → still loading (cross-org fan-out). In edit mode for an
	//     already-recurring event, stay OPTIMISTIC so the defensive reset can't
	//     wipe the freshly-hydrated `recurring=true` before perm resolves; in
	//     create mode (or non-recurring edits) fall back to page perm.
	const permIsResolved = typeof effectiveCanManageRecurrent === 'boolean';
	const wasRecurringAtMount = !!initialFormState?.recurring;
	const canManageRecurrent = permIsResolved
		? effectiveCanManageRecurrent
		: isEditMode && wasRecurringAtMount
			? true
			: pageCanManageRecurrent;

	const sortByLabel = arr =>
		arr
			.slice()
			.sort((a, b) =>
				a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' })
			);

	// Form's effective org — initialized from the prop but user-editable via
	// the Organization Autocomplete. All dependent dropdowns (locations,
	// pilots, instructors, resources) filter on this, so changing the org
	// updates them in lockstep.
	//
	// IMPORTANT: ALL primitive form `useState`/`useRef` declarations live
	// HERE, BEFORE any `useMemo`/`useCallback` that reads them. Reordering
	// (e.g. moving `locationId` below the `resources` useMemo) re-introduces
	// the TDZ ReferenceError that broke modal mount.
	const [formOrgUnique, setFormOrgUnique] = useState(orgUnique || '');
	const [eventType, setEventType] = useState('air');
	// CalendarDateTime values from `@internationalized/date` (or null). The
	// AppDatePicker wraps `react-datepicker` and emits these objects via
	// `onChange`. We convert at submit time via `cdtToEpoch` below.
	const [startTime, setStartTime] = useState(null);
	const [finishTime, setFinishTime] = useState(null);
	const [locationId, setLocationId] = useState('');
	const [pilotId, setPilotId] = useState('');
	const [sicPilotId, setSicPilotId] = useState('');
	const [instructorId, setInstructorId] = useState('');
	// Activity Type — bare ID string ('' = "Not set"). Optional; regular events
	// only (day-view parity). Reset on event-type switch and re-seeded by the
	// hydration effects.
	const [activityType, setActivityType] = useState('');
	// Multi-resource: legacy modal allows multiple resources of any type
	// (Aircraft + Headphones + Simulators) for regular events; maintenance
	// is capped to a single item. Backend `FilteredResourceDto` doesn't ship
	// `typeName` yet — once it does, the grouped render below will start
	// bucketing items automatically without further code changes.
	const [resourceIds, setResourceIds] = useState([]);
	const [comments, setComments] = useState('');
	// Event Sharing — "invite pilots to participate" flag (day-view parity).
	// Boolean; false by default. Regular events only; re-seeded by hydration.
	const [eventSharing, setEventSharing] = useState(false);
	const [recurring, setRecurring] = useState(false);
	// Recurrence sub-form state — kept as strings for the inputs; converted
	// at submit time. `daysOfWeek` carries Sunday-indexed string keys '0'..'6'
	// to match the legacy AngularJS modal and the Symfony DTO contract.
	const [repsInterval, setRepsInterval] = useState('1');
	const [recurringType, setRecurringType] = useState('day');
	const [daysOfWeek, setDaysOfWeek] = useState([]);
	const [repsType, setRepsType] = useState('number');
	const [reps, setReps] = useState('1');
	const [recurringEndDate, setRecurringEndDate] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	// Live view of isOpen for async pre-check responses (stale-closure-proof).
	const openRef = useRef(isOpen);
	useEffect(() => {
		openRef.current = isOpen;
	}, [isOpen]);
	const reducedMotion = useReducedMotion();
	// Custom field values keyed by field id.
	const [customValues, setCustomValues] = useState({});
	// Conflict state from `/calendar/check-event-params`. The `resources` Set
	// carries IDs the backend marked `used: true` for the current form
	// snapshot (chip markers still use Set lookup). `errors` is a flat list
	// of human-readable messages from the response envelope.
	//
	// Pilot / SIC / Instructor busy state is NOT tracked here anymore —
	// dropdowns are driven directly by `candidates` below (response-derived,
	// each item carries `used` inline; matches legacy
	// `state.eventModalData.users.{pilots,coPilots,instructors}` parity).
	// `errors` = lines to list; `instructorsBlocker` = the response's instructor
	// blocker lines (absent on resets/4xx paths, read with `?.`).
	const [conflicts, setConflicts] = useState({
		resources: new Set(),
		errors: [],
		instructorsBlocker: []
	});
	// { [resourceId: string]: string[] } — "blocked by" labels from precheck response.
	// HARD blockers only (access/instructor/resource-blocked-by-resource) — this is
	// what gates submit. Maintenance is informational-only and must never land here
	// (see `maintenanceInfoMap` below).
	const [blockedByMap, setBlockedByMap] = useState({});
	// { [resourceId: string]: string[] } — "is on maintenance from…" info strings from
	// precheck response. Display-only: legacy treats maintenance as pure display on
	// every surface (`Calendar.php:2990-2997` returns success:true + the maintenance
	// string regardless of `addToWaiting`), it never gates a reservable aircraft/sim.
	const [maintenanceInfoMap, setMaintenanceInfoMap] = useState({});
	// Response-driven dropdown items for Pilot / SIC / Instructor.
	// Populated by the precheck response handler from
	// `data.users.{pilots,coPilots,instructors}`. Each item already carries
	// `used: bool` and `fullName` / `name` / `surname` for label, so the
	// renderer reads them directly without the previous Set + master-list
	// indirection.
	const [candidates, setCandidates] = useState({
		pilots: [],
		sics: [],
		instructors: []
	});
	// Monotonic id used to drop stale validation responses (user kept typing).
	const validateReqIdRef = useRef(0);
	// Track the last error string we toasted so we don't spam the same message
	// on every keystroke that re-runs validation.
	const lastToastedErrorRef = useRef('');
	// HW-1081: the Pilot the form filled in by itself (current user), as opposed to
	// one the user picked. Only an auto-filled Pilot is dropped when the pre-check
	// says the organization has no such pilot.
	const autoPilotIdRef = useRef('');
	// First eligible precheck after `isOpen` flips true skips the 600 ms
	// debounce so Edit-mode forms have conflict markers ready before the user
	// touches any input.
	const firstPrecheckDoneRef = useRef(false);
	// Edit-mode scope for an already-recurring event: 'current' | 'not-started' | 'following'.
	// Mirrors `EDIT_RECURRING_OPTIONS` in RecurringScopeConfirm.js (legacy parity).
	// `all` is delete-only and intentionally absent here. Default 'following'.
	const [recurringScope, setRecurringScope] = useState('following');
	// Waiting-list flip state. `precheckSettled` flips true once the first
	// precheck response lands, handing control from the open-time reserve seed
	// to the reactive `latestPrecheckSignal`. Both reset on every modal open so
	// a prior session's signal can't leak (see hydration effect).
	const [precheckSettled, setPrecheckSettled] = useState(false);
	const [latestPrecheckSignal, setLatestPrecheckSignal] = useState(false);
	// Tracks whether the hydration effect ran at least once. Guards the
	// defensive `canManageRecurrent` reset effect so async perms-load can't
	// wipe the freshly-hydrated `recurring=true` before perms resolve.
	const hasHydratedRef = useRef(false);
	// Resource Autocomplete autoclose — final approach after Rem20 Sub 6b
	// (microtask defer), Rem22 Sub 9 (blur), and Rem24 Sub 7 (key bump).
	// Key bump on every selection fully unmounts HeroUI's internal open
	// state so the dropdown closes deterministically. Blur ref is kept as
	// belt-and-suspenders. Do NOT revert to controlled `isOpen` mode —
	// races with HeroUI's internal focus reopen.
	const resourceInputRef = useRef(null);
	const [resourcePickerKey, setResourcePickerKey] = useState(0);

	// Mutual-exclusion mapping for Pilot / SIC / Instructor dropdowns. Sub
	// `b4a1cd1e` introduced `disabledKeys` but mixed id types:
	//   - `pilotId` / `sicPilotId` form-state = USER id (matches pilots/sics item.id)
	//   - `instructorId` form-state         = RESOURCE id (matches instructors item.id)
	//   - `candidates.instructors[].instructorId` = USER id behind the resource
	// Mapping must translate between user-id and resource-id when crossing
	// the Pilot/SIC ↔ Instructor boundary, otherwise the disable never matches.
	const instructorUserId = useMemo(() => {
		if (!instructorId) return null;
		const item = (candidates.instructors || []).find(i => String(i.id) === String(instructorId));
		return item?.instructorId ? String(item.instructorId) : null;
	}, [instructorId, candidates.instructors]);
	const instructorDisabledKeys = useMemo(() => {
		const excludeUserIds = [pilotId, sicPilotId].filter(Boolean).map(String);
		if (excludeUserIds.length === 0) return [];
		return (candidates.instructors || [])
			.filter(i => excludeUserIds.includes(String(i.instructorId)))
			.map(i => String(i.id));
	}, [pilotId, sicPilotId, candidates.instructors]);

	const organizations = useMemo(() => {
		if (!organizationsMap) return [];
		const values = Array.isArray(organizationsMap)
			? organizationsMap
			: Object.values(organizationsMap);
		return sortByLabel(
			values
				.filter(o => o && !o.trash)
				.map(o => ({
					id: String(o.organizationUnique ?? o.unique ?? ''),
					label: o.organizationName ?? o.name ?? ''
				}))
				.filter(o => o.id && o.label)
		);
	}, [organizationsMap]);

	// Rem30 Item 4: display name for the locked organization. The org picker
	// is now a static read-only field, so we only need the label for the
	// current `formOrgUnique` — resolved from the same adapted list the
	// dropdown used.
	const formOrgName = useMemo(() => {
		const hit = organizations.find(o => o.id === formOrgUnique);
		return hit ? hit.label : '';
	}, [organizations, formOrgUnique]);

	const locations = useMemo(() => {
		if (!locationsMap) return [];
		const values = Array.isArray(locationsMap) ? locationsMap : Object.values(locationsMap);
		return sortByLabel(
			values
				.filter(x => x && !x.trash)
				.filter(x => !formOrgUnique || x.organizationUnique === formOrgUnique)
				.map(x => ({
					id: String(x.locationID ?? x.id),
					label: x.locationName ?? x.name ?? ''
				}))
				.filter(x => x.label)
		);
	}, [locationsMap, formOrgUnique]);

	// 4-tier fallback: selected location tz → edit-detail tz → filter tz → UTC.
	// Edit-detail tz (from initialFormState.locationTimezoneId) covers the
	// window before refs finish loading.
	const eventTimeZone = useMemo(() => {
		return (
			locationsMap?.[locationId]?.timeZoneId ||
			initialFormState?.locationTimezoneId ||
			timezone ||
			'UTC'
		);
	}, [locationsMap, locationId, initialFormState?.locationTimezoneId, timezone]);

	// Pilots/Instructors bridge maps carry separate name + surname fields.
	// Always concat both so the autocomplete shows "First Last" instead of
	// truncating to first-name only.
	//
	// Scoped to `formOrgUnique` — these arrays now feed only label/lookup
	// fallbacks; the live Pilot / SIC / Instructor dropdowns are populated
	// from the `candidates` state (response-driven). The Round 2 attempt to
	// drop this filter for the dropdown-visibility case is no longer needed.
	const toPeople = map => {
		if (!map) return [];
		const values = Array.isArray(map) ? map : Object.values(map);
		return sortByLabel(
			values
				.filter(x => x && !x.trash)
				.filter(x => !formOrgUnique || x.organizationUnique === formOrgUnique)
				.map(x => {
					const name = x.pilotName ?? x.name ?? x.firstName ?? '';
					const surname = x.pilotSurname ?? x.surname ?? x.lastName ?? '';
					return {
						id: String(x.id ?? x.userID ?? x._userID ?? ''),
						label: [name, surname].filter(Boolean).join(' ')
					};
				})
				.filter(x => x.id && x.label)
		);
	};
	const instructors = useMemo(() => toPeople(instructorsMap), [instructorsMap, formOrgUnique]);

	// Custom fields are per-org. Bridge stores them keyed by id with
	// `organizationUnique` injected (`bridgeReducers.js:331`). Filter to the
	// form's chosen org so picking a different org swaps fields cleanly.
	// Ordering: compact (numeric/date) first, then text — combined with the
	// `col-span-2` rule on `text` below, this places numeric+date on the
	// first row and text full-width on its own row.
	const customFields = useMemo(() => {
		if (!customFieldsMap) return [];
		const values = Array.isArray(customFieldsMap)
			? customFieldsMap
			: Object.values(customFieldsMap);
		const mapped = values
			.filter(cf => cf && !cf.trash)
			.filter(
				cf => !formOrgUnique || !cf.organizationUnique || cf.organizationUnique === formOrgUnique
			)
			.map(cf => ({
				id: String(cf.id),
				name: cf.name || `Field ${cf.id}`,
				type: String(cf.type || 'text').toLowerCase(),
				required: cf.required === true || cf.required === 1 || cf.required === '1'
			}));
		const weight = t => (t === 'text' ? 1 : 0);
		return mapped.sort((a, b) => weight(a.type) - weight(b.type));
	}, [customFieldsMap, formOrgUnique]);

	// Rem32 Task 1 — non-trashed activity-type options for the Activity Type
	// selector. `activityTypesMap` (Rem30 Sub 5) is already org-scoped at fetch
	// time (org-scoped endpoint + Rem30 Task 3 locks the calendar to one org),
	// so no extra `formOrgUnique` filter is required — and the entries do not
	// carry `organizationUnique`. `{ value, label }` mirrors the day-view
	// `getActivityTypesOptions` (`calendar.model.js:345-356`).
	const activeActivityTypeOptions = useMemo(
		() =>
			sortByLabel(
				Object.values(activityTypesMap)
					.filter(at => at && !at.trash)
					.map(at => ({ value: String(at.id), label: at.name || '' }))
			),
		[activityTypesMap]
	);

	const resources = useMemo(() => {
		if (!resourcesMap) return [];
		// Legacy parity: resources cascade off Location. Until the user picks
		// one, show nothing — otherwise the dropdown is overwhelming and
		// mixes aircraft from every location in the org.
		if (!locationId) return [];
		const values = Array.isArray(resourcesMap) ? resourcesMap : Object.values(resourcesMap);
		// Match by all known location-id fields: legacy `locationID`, Symfony
		// `locationId` (camelCase) and the nested `location.id` shape. Some
		// resource types in the bridge may carry a null locationID — keep
		// them too so they're not silently filtered out (org-wide items).
		const locIdStr = String(locationId);
		const resourceLocId = r => {
			const candidate = r.locationID ?? r.locationId ?? r.location?.id ?? null;
			return candidate == null ? null : String(candidate);
		};
		return sortByLabel(
			values
				.filter(r => r && !r.trash)
				// Mirror `adaptResources` fallback (`utils/monthFilterDataAdapter.js:79`)
				// — Symfony `FilteredResourceDto` returns `organizationUnique: null`
				// for global/org-wide items. CalendarFilterBar handles this via
				// `?? fallbackOrgUnique`; CreateEventModal must do the same here,
				// otherwise every global resource gets silently dropped and the
				// dropdown shows "Resources не знайдено".
				.filter(r => {
					if (!formOrgUnique) return true;
					const orgUnique = r.organizationUnique ?? formOrgUnique;
					return orgUnique === formOrgUnique;
				})
				.filter(r => {
					const lid = resourceLocId(r);
					return lid == null || lid === locIdStr;
				})
				// Instructors live in the same resources bridge map (refsSlice
				// merges aircraft + instructors into one `resources` array; the
				// `instructorsMapBridge` re-splits them downstream). Belt-and-
				// braces filter: drop anything with `instructorName` set OR a
				// `type` that mentions instructor — covers all bridge shapes.
				.filter(r => {
					if (r.instructorName != null) return false;
					const t =
						typeof r.type === 'object' ? (r.type && r.type.name) || '' : String(r.type || '');
					return !t.toLowerCase().includes('instructor');
				})
				.map(r => {
					// `typeName` / `typeID` come from the backend once Symfony
					// adds them to `FilteredResourceDto` — until then they're
					// undefined and the dropdown renders as a single flat group.
					// Handle THREE shapes: object `r.type={name,id}`, string
					// `r.type='aircraft'` (legacy/refs fallback), and explicit
					// `r.typeName/typeID` fields.
					const typeName =
						(typeof r.type === 'object' ? r.type?.name : r.type) ||
						r.typeName ||
						r.resourceTypeName ||
						'';
					const typeID =
						(typeof r.type === 'object' ? r.type?.id : r.typeID) || r.resourceTypeID || null;
					return {
						id: String(r.resourceID ?? r.id),
						label: r.resourceName ?? r.name ?? '',
						typeName: typeName || '',
						typeID: typeID != null ? String(typeID) : ''
					};
				})
		);
	}, [resourcesMap, formOrgUnique, locationId]);

	// Group resources by typeName for the dropdown sections. Falls back to
	// a single ungrouped bucket when the backend hasn't shipped `typeName`
	// yet so the UI still works. Rem20 Sub 5: groups sorted by domain
	// priority (Aircraft → Simulator → Headphone(s) → Classroom → other) so
	// the most-used types surface first instead of backend insertion order.
	const resourceGroups = useMemo(() => {
		if (resources.length === 0) return [];
		const hasGroups = resources.some(r => r.typeName);
		if (!hasGroups) return [{ key: '__all__', title: '', items: resources }];
		const buckets = new Map();
		resources.forEach(r => {
			const key = r.typeID || r.typeName || '__other__';
			if (!buckets.has(key)) buckets.set(key, { key, title: r.typeName || 'Other', items: [] });
			buckets.get(key).items.push(r);
		});
		return sortResourceGroupsByPriority(Array.from(buckets.values()));
	}, [resources]);

	// Rem24 Sub 8 — aircraft-cap helpers. Business rule: ONE aircraft per
	// event. Used to gate `onSelectionChange` against a second aircraft and
	// to populate the Autocomplete's `disabledKeys` so the other aircraft
	// items render dimmed in the dropdown.
	const aircraftResourceIds = useMemo(() => {
		const ids = new Set();
		resources.forEach(r => {
			if (
				String(r.typeName || '')
					.toLowerCase()
					.startsWith('aircraft')
			) {
				ids.add(String(r.id));
			}
		});
		return ids;
	}, [resources]);
	const selectedAircraftId = useMemo(() => {
		return resourceIds.find(id => aircraftResourceIds.has(String(id))) || null;
	}, [resourceIds, aircraftResourceIds]);
	const disabledAircraftKeys = useMemo(() => {
		if (!selectedAircraftId) return [];
		return Array.from(aircraftResourceIds).filter(id => id !== String(selectedAircraftId));
	}, [aircraftResourceIds, selectedAircraftId]);

	// `eventDefaultLength` is the org/user setting (in seconds). Sourced from
	// the bootstrap slice via the org-aware selector — recomputes whenever
	// the user picks a different org in the Organization Autocomplete, so
	// the End Time auto-shift below stays in sync with the picked org.
	// Precedence: user override → orgSettingsMap[formOrg] → dashboard → 3600.
	const eventDefaultLengthSec = useSelector(state =>
		selectEventDefaultLengthSec(state, formOrgUnique)
	);
	const canManageMaintenance = useSelector(
		state => getEventOrgPerms(state, formOrgUnique)?.canManageMaintenance ?? false
	);
	// `calendarManageAnothers` for the form org — part of the conditional
	// Pilot-required rule (day-view parity: `calendar.event-add.js:506`).
	const canManageAnothers = useSelector(
		state => getEventOrgPerms(state, formOrgUnique)?.canManageAnothers ?? false
	);
	const isOrgOverdue = useSelector(state => !!state?.organizationsAccess?.[formOrgUnique]?.overdue);
	// `useStore` lets the init `useEffect` read the selector imperatively
	// keyed on the page `orgUnique` prop instead of the leftover
	// `formOrgUnique` state — HeroUI Modal hides via `isOpen` and never
	// unmounts the instance, so closing+reopening would otherwise carry the
	// previous session's chosen org into the End Time auto-derive.
	const store = useStore();

	useEffect(() => {
		if (!isOpen) {
			// Reset on close so the next open re-hydrates cleanly.
			hasHydratedRef.current = false;
			return;
		}
		// Reset the waiting-list flip on every open so the reserve seed governs
		// the first precheck and a stale signal from a prior session can't leak.
		setPrecheckSettled(false);
		setLatestPrecheckSignal(false);
		if (isEditMode) {
			// Edit: hydrate from `initialFormState` (already produced via
			// `mapEventToFormState` by the parent EventViewModal). Reset
			// transient UI state but DO NOT touch the form values.
			const fs = initialFormState || {};
			setFormOrgUnique(fs.formOrgUnique || eventOrgUnique || orgUnique || '');
			setEventType(fs.eventType || 'air');
			onEventTypeChange?.(fs.eventType || 'air');
			setStartTime(fs.startTime || null);
			setFinishTime(fs.finishTime || null);
			setLocationId(fs.locationId || '');
			setPilotId(fs.pilotId || '');
			autoPilotIdRef.current = '';
			setSicPilotId(fs.sicPilotId || '');
			setInstructorId(fs.instructorId || '');
			setActivityType(fs.activityType || '');
			setResourceIds(Array.isArray(fs.resourceIds) ? fs.resourceIds : []);
			setComments(fs.comments || '');
			setEventSharing(!!fs.eventSharing);
			setRecurring(!!fs.recurring);
			setRepsInterval(fs.repsInterval || '1');
			setRecurringType(fs.recurringType || 'day');
			setDaysOfWeek(Array.isArray(fs.daysOfWeek) ? fs.daysOfWeek : []);
			setRepsType(fs.repsType || 'number');
			setReps(fs.reps || '1');
			setRecurringEndDate(fs.recurringEndDate || null);
			setCustomValues(fs.customValues || {});
			setConflicts({ resources: new Set(), errors: [] });
			// Seed the assigned pilot/SIC/instructor as dropdown candidates from
			// the refs maps so their chips render immediately — even for a
			// checked-out event whose `check-event-params` omits them (Task:20).
			setCandidates(
				buildAssignedCandidates(pilotsMap, instructorsMap, {
					pilotId: fs.pilotId,
					sicPilotId: fs.sicPilotId,
					instructorId: fs.instructorId
				})
			);
			setRecurringScope('following');
			lastToastedErrorRef.current = '';
			hasHydratedRef.current = true;
			return;
		}
		// Create + `initialFormState` (Waiting List flow) — hydrate from the
		// supplied form state (cloned from a view-modal event) with two
		// overrides: Pilot snaps to the current user (legacy parity); recurring
		// fields are cleared (waiting-list entries are single occurrences).
		if (initialFormState) {
			const fs = initialFormState;
			setFormOrgUnique(fs.formOrgUnique || orgUnique || '');
			setEventType(fs.eventType || 'air');
			onEventTypeChange?.(fs.eventType || 'air');
			setStartTime(fs.startTime || null);
			setFinishTime(fs.finishTime || null);
			setLocationId(fs.locationId || '');
			setPilotId(currentUserId != null ? String(currentUserId) : fs.pilotId || '');
			autoPilotIdRef.current = currentUserId != null ? String(currentUserId) : '';
			setSicPilotId(fs.sicPilotId || '');
			setInstructorId(fs.instructorId || '');
			setActivityType(fs.activityType || '');
			setResourceIds(Array.isArray(fs.resourceIds) ? fs.resourceIds : []);
			setComments(fs.comments || '');
			setEventSharing(!!fs.eventSharing);
			setRecurring(false);
			setRepsInterval('1');
			setRecurringType('day');
			setDaysOfWeek([]);
			setRepsType('number');
			setReps('1');
			setRecurringEndDate(null);
			setCustomValues(fs.customValues || {});
			setConflicts({ resources: new Set(), errors: [] });
			// Pilot snapped to the current user above — seed it (plus any cloned
			// SIC/instructor) as candidates so the author chip renders (Task:20).
			setCandidates(
				buildAssignedCandidates(pilotsMap, instructorsMap, {
					pilotId: currentUserId != null ? String(currentUserId) : fs.pilotId,
					sicPilotId: fs.sicPilotId,
					instructorId: fs.instructorId
				})
			);
			setRecurringScope('following');
			lastToastedErrorRef.current = '';
			hasHydratedRef.current = true;
			return;
		}
		const d = initialDayTs ? new Date(Number(initialDayTs) * 1000) : new Date();
		if (Number.isNaN(d.getTime())) return;

		// A view with time-slot granularity (Week) hands over the exact instant the
		// user clicked, already resolved against the ORGANIZATION's timezone. Use it
		// verbatim: `fromDate` below converts an instant into `eventTimeZone`, so a
		// locally-assembled wall clock would arrive shifted by the whole offset —
		// which is why clicking any Week cell used to produce the same wrong hour.
		// `null` means "no slot" (Week's "+ Book" and Create Event): Number(null)
		// is 0, which opened the form on 31 Dec 1969.
		const clickedTs = initialStartTs == null ? NaN : Number(initialStartTs);
		const clicked = Number.isFinite(clickedTs) ? new Date(clickedTs * 1000) : null;

		// Month has no slots (day click, not slot click), so it keeps the previous
		// default: the current wall-clock time rounded up to the next hour.
		const now = new Date();
		const defaultHour = now.getMinutes() > 0 ? Math.min(now.getHours() + 1, 23) : now.getHours();
		const startD =
			clicked && !Number.isNaN(clicked.getTime())
				? clicked
				: new Date(d.getFullYear(), d.getMonth(), d.getDate(), defaultHour, 0, 0);
		// Read the selector imperatively at open-time using the page
		// `orgUnique` prop, NOT the leftover `formOrgUnique` from a prior
		// modal session. The render-time `eventDefaultLengthSec` above is
		// still consumed by the Start Time onChange handler so a user-
		// switched org takes effect after open.
		const initialLengthSec = selectEventDefaultLengthSec(store.getState(), orgUnique);
		const endD = new Date(startD.getTime() + initialLengthSec * 1000);
		setStartTime(fromDate(startD, eventTimeZone));
		setFinishTime(fromDate(endD, eventTimeZone));
		setFormOrgUnique(orgUnique || '');
		setEventType('air');
		onEventTypeChange?.('air');
		// UX: when the org has exactly one location, auto-select it so the
		// user is not forced into a one-option pick. `locations` is the
		// org-filtered memo — bootstrap-loaded and reliable at open-time (same
		// reasoning as the Pilot auto-fill below; `formOrgUnique` is locked to
		// the page org so the memo is already correctly scoped). The dependent
		// timezone memo and the resources cascade pick the value up
		// automatically — no extra handler wiring needed.
		setLocationId(locations.length === 1 ? locations[0].id : '');
		// Legacy parity: auto-fill Pilot with current user if they appear in
		// the pilots list (i.e. have the pilot role). Otherwise leave empty.
		// `pilots` intentionally NOT in deps — it changes whenever the form's
		// org changes, and re-running this effect would clobber the user's
		// org pick. Pilots are bootstrap-loaded so reading them at open is
		// reliable. Same reasoning for `currentUserId` (stable per session).
		// Type-tolerant id compare (Task:20): `currentUserId` may be a number and
		// `p.id` a string (or vice-versa) — `String()` both sides so the author
		// match is robust to backend DTO id-type changes.
		// HW-1081: checked against the page org, not the `pilots` memo (see isPilotInOrg).
		const mePilotId = isPilotInOrg(currentUserId, orgUnique) ? String(currentUserId) : '';
		autoPilotIdRef.current = mePilotId;
		setPilotId(mePilotId);
		setSicPilotId('');
		setInstructorId('');
		setActivityType('');
		setResourceIds([]);
		setComments('');
		setEventSharing(false);
		setRecurring(false);
		// Recurrence fields reset to defaults — must not leak between
		// modal sessions even if the user closed the modal mid-edit.
		setRepsInterval('1');
		setRecurringType('day');
		setDaysOfWeek([]);
		setRepsType('number');
		setReps('1');
		setRecurringEndDate(null);
		setCustomValues({});
		setConflicts({ resources: new Set(), errors: [] });
		// Seed the auto-filled author pilot as a candidate so its chip renders
		// immediately (before/without `check-event-params`) — Task:20 Symptom A.
		setCandidates(buildAssignedCandidates(pilotsMap, instructorsMap, { pilotId: mePilotId }));
		setRecurringScope('following');
		lastToastedErrorRef.current = '';
		hasHydratedRef.current = true;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, initialDayTs, orgUnique, isEditMode, initialFormState, eventOrgUnique]);

	// Rem30 Item 4: the organization is locked to the dashboard org and the
	// picker is now a static read-only field — the user can no longer change
	// the org, so the former `handleOrgChange` reset handler was removed. The
	// `formOrgUnique` state is still seeded from the `orgUnique` prop by the
	// hydration effects below; only the UI control changed.

	const setCustomValue = (id, value) => setCustomValues(prev => ({ ...prev, [id]: value }));

	// ─── Recurrence handlers ────────────────────────────────────────────────
	const resetRecurrenceFields = useCallback(() => {
		setRepsInterval('1');
		setRecurringType('day');
		setDaysOfWeek([]);
		setRepsType('number');
		setReps('1');
		setRecurringEndDate(null);
	}, []);

	// Defensive reset if `canManageRecurrent` flips to false mid-session
	// (rare — e.g. role change while modal stays mounted). Hides the leftover
	// recurrence state so a subsequent re-grant of the permission starts
	// from a clean slate instead of resurrecting whatever was last typed.
	// Guarded on `hasHydratedRef` so the initial async perms-load window in
	// Edit mode can't wipe the freshly-hydrated `recurring=true` before
	// `effectiveCanManageRecurrent` resolves.
	useEffect(() => {
		if (!hasHydratedRef.current) return;
		if (!canManageRecurrent && recurring) {
			setRecurring(false);
			resetRecurrenceFields();
		}
	}, [canManageRecurrent, recurring, resetRecurrenceFields]);

	// Wrap the checkbox setter so unchecking fully resets the sub-form.
	// Reset is in the setter (not lazy on submit) per task DoD.
	const handleRecurringChange = useCallback(
		checked => {
			setRecurring(checked);
			if (!checked) resetRecurrenceFields();
		},
		[resetRecurrenceFields]
	);

	// Mirrors legacy `changeRecurringType` (calendar-event.js:2203-2221) —
	// when switching to 'weeks', auto-default `daysOfWeek` to the event's
	// start weekday so the user has something selected (legacy invariant:
	// can't leave 0 days picked once weeks is active).
	const handleRecurringTypeChange = useCallback(
		nextType => {
			setRecurringType(nextType);
			if (nextType === 'weeks') {
				const e = startTime ? cdtToEpoch(startTime, eventTimeZone) : null;
				const startCdt = e != null ? epochToCalendarDateTime(e, eventTimeZone) : null;
				// `getDayOfWeek(date, 'en-US')` returns Sunday=0..Saturday=6,
				// matching the `WEEK_DAYS` keys. `CalendarDateTime` has no
				// `dayOfWeek` property — the old `startCdt.dayOfWeek % 7`
				// computed `undefined % 7 = NaN`.
				const dow = startCdt ? getDayOfWeek(startCdt, 'en-US') : new Date().getDay();
				setDaysOfWeek([String(dow)]);
			} else {
				setDaysOfWeek([]);
			}
		},
		[startTime, eventTimeZone]
	);

	// Port of `calendar.event-recurring.js:128-149` "min 1 selected" rule:
	// the chip click handler refuses to uncheck the last selected day.
	const toggleDayOfWeek = useCallback(dayKey => {
		setDaysOfWeek(prev => {
			const isOn = prev.includes(dayKey);
			if (isOn) {
				if (prev.length <= 1) return prev; // cannot uncheck last
				return prev.filter(d => d !== dayKey);
			}
			return [...prev, dayKey].sort();
		});
	}, []);

	// Live Summary line — shared with the View modal via `formatRecurrenceSummary`.
	const summaryText = useMemo(
		() =>
			formatRecurrenceSummary(
				{
					recurring,
					repsInterval,
					recurringType,
					daysOfWeek,
					repsType,
					reps,
					recurringEndDate,
					startTime
				},
				eventTimeZone
			),
		[
			recurring,
			repsInterval,
			recurringType,
			daysOfWeek,
			repsType,
			reps,
			recurringEndDate,
			startTime,
			eventTimeZone
		]
	);

	const isMaintenance = eventType === 'maintenance';

	// Single effective addToWaiting flag fed to every payload emit + the submit
	// label. Waiting list is an aircraft/simulator concept, so maintenance
	// events force it off. Before the first precheck the open-time reserve seed
	// governs (so an edited waiting-list event saves as waiting even if the user
	// submits immediately); after, the bidirectional precheck signal takes over.
	const initialReserveSeed = !isMaintenance && !!initialFormState?.reserve;
	const effectiveAddToWaiting = isMaintenance
		? false
		: computeEffectiveAddToWaiting({
				forceAddToWaiting,
				precheckSettled,
				latestPrecheckSignal,
				initialReserveSeed
			});

	const orgInvalid = !formOrgUnique;
	const locationInvalid = !locationId;
	// Day-view parity (`calendar.event-add.js:506`): Pilot is OPTIONAL only when
	// the org enables `pilotOptional` AND the current user can manage others'
	// events OR is the event's selected instructor. `instructorUserId` is the
	// USER id behind the selected instructor resource (see L313-317), matching
	// legacy `isUserEventInstructor`.
	const isUserSelectedInstructor =
		instructorUserId != null &&
		currentUserId != null &&
		String(instructorUserId) === String(currentUserId);
	const pilotIsOptional = pilotOptional && (canManageAnothers || isUserSelectedInstructor);
	const pilotInvalid = !isMaintenance && !pilotId && !pilotIsOptional;
	const resourceInvalid = resourceIds.length === 0 && (isMaintenance || !instructorId);
	const commentsInvalid = enforseComments && (!comments || !comments.trim());
	const startTimeInvalid = !startTime;
	const endTimeInvalid = !finishTime;
	const isFormInvalid = useMemo(() => {
		if (orgInvalid || locationInvalid || pilotInvalid || resourceInvalid || commentsInvalid)
			return true;
		if (startTimeInvalid || endTimeInvalid) return true;
		if (!isMaintenance) {
			for (const cf of customFields) {
				if (!cf.required) continue;
				const v = customValues[cf.id];
				const empty =
					v == null ||
					(typeof v === 'string' && !v.trim()) ||
					(typeof v === 'object' && cf.type === 'date' && v?.year == null);
				if (empty) return true;
			}
		}
		return false;
	}, [
		orgInvalid,
		locationInvalid,
		pilotInvalid,
		resourceInvalid,
		commentsInvalid,
		startTimeInvalid,
		endTimeInvalid,
		isMaintenance,
		customFields,
		customValues
	]);

	// Hard blockers: per-resource strings from the precheck `blockedBy` /
	// `maintenance` arrays. Derived reactively so the submit gate and field
	// highlights clear automatically once the user resolves the issue and the
	// next precheck runs.
	const activeHardBlockers = resourceIds.flatMap(id => blockedByMap[String(id)] || []);
	const hasHardBlocker = activeHardBlockers.length > 0;
	const instructorHardBlocker =
		activeHardBlockers.some(b => /instructor/i.test(b)) ||
		(conflicts.instructorsBlocker?.length ?? 0) > 0;

	// Lookup helpers: build the inline conflict hint for the currently
	// selected pilot / sic / instructor / resource. Mirrors the legacy
	// modal's `"<Name> – The <kind> is busy at that time"` template.
	const findLabel = (list, id) => {
		if (!id) return '';
		const m = list.find(it => String(it.id) === String(id));
		return m ? m.label : '';
	};
	// Candidate-driven lookup for Pilot / SIC / Instructor — items come from
	// the precheck response and carry `used` + `fullName` / `name` /
	// `surname` directly, so the hint reads them straight off the response
	// item without cross-referencing the legacy refs-derived list.
	const candidateItem = (list, id) => {
		if (!id) return null;
		return list.find(it => String(it.id) === String(id)) || null;
	};
	const candidateLabel = item => {
		if (!item) return '';
		return (item.fullName || '').trim() || `${item.name || ''} ${item.surname || ''}`.trim();
	};
	const conflictHint = (kind, label) =>
		label ? `${label} – The ${kind} is busy at that time` : null;
	const pilotCandidate = candidateItem(candidates.pilots, pilotId);
	const sicCandidate = candidateItem(candidates.sics, sicPilotId);
	const instructorCandidate = candidateItem(candidates.instructors, instructorId);
	const pilotHint = pilotCandidate?.used
		? conflictHint('pilot', candidateLabel(pilotCandidate))
		: null;
	const sicHint = sicCandidate?.used
		? conflictHint('SIC pilot', candidateLabel(sicCandidate))
		: null;
	const instructorHint = instructorCandidate?.used
		? conflictHint('instructor', candidateLabel(instructorCandidate))
		: instructorCandidate?.unavailable
			? `${candidateLabel(instructorCandidate)} – Not available at this time`
			: null;
	const resourceHint = (() => {
		const conflictingLabels = (resourceIds || [])
			.filter(id => conflicts.resources.has(String(id)))
			.map(id => findLabel(resources, id))
			.filter(Boolean);
		if (!conflictingLabels.length) return null;
		return `${conflictingLabels.join(', ')} ${conflictingLabels.length === 1 ? 'is' : 'are'} already booked at this time`;
	})();

	// Renders a Pilot / SIC / Instructor AutocompleteItem from a
	// `check-event-params` response item. Busy entities get a right-aligned
	// HeroUI `Chip` "Busy" badge; free entities render the plain label.
	// `fullName` is the preferred display label with `name surname` as a
	// fallback.
	const renderItem = item => {
		// `.trim()` on fullName matters: a user with blank first/last name (a
		// registration not yet completed) arrives as fullName " " — truthy, so
		// the old `||` chain rendered a visually EMPTY row (tester). Fall all
		// the way to the id, same last resort the filter's buildGroups uses.
		const label =
			(item.fullName || '').trim() ||
			`${item.name || ''} ${item.surname || ''}`.trim() ||
			`#${item.id}`;
		return (
			<AutocompleteItem key={item.id} textValue={label}>
				<div className="flex items-center justify-between gap-2">
					<span>{label}</span>
					{item.used && (
						<Chip size="sm" color="danger" variant="flat" className="flex-shrink-0">
							Busy
						</Chip>
					)}
					{!item.used && item.unavailable && (
						<Chip size="sm" color="warning" variant="flat" className="flex-shrink-0">
							Not available
						</Chip>
					)}
				</div>
			</AutocompleteItem>
		);
	};

	// ─── Precheck (conflicts + candidates) ──────────────────────────────────
	// Call to `/calendar/check-event-params`. Two responsibilities:
	//   1. Build the `conflicts.resources` Set + `errors` for chip markers
	//      and the inline error toast.
	//   2. Populate response-driven `candidates.{pilots,sics,instructors}`
	//      that feed the Pilot / SIC / Instructor dropdowns directly.
	// First eligible run after `isOpen` flips true fires immediately
	// (`firstPrecheckDoneRef`); subsequent edits stay debounced 600 ms.
	// Backend remains the source of truth at save.
	useEffect(() => {
		if (!isOpen) {
			// Reset so the next open fires immediately again.
			firstPrecheckDoneRef.current = false;
			return undefined;
		}
		// Stale-display guard — `check-event-params` can take ~10s. Without
		// this, when the user selects aircraft A → deselects → selects
		// aircraft B in rapid succession, the previous response's
		// `blockedBy` text (e.g. "AS 350: blocked by Aircraft: A310") keeps
		// showing under the chip until the new response lands, leading the
		// user to think the original aircraft is still attached. Clearing
		// the stale state on every resourceIds change makes the UI honestly
		// reflect "pending validation" until the new response arrives.
		setConflicts({ resources: new Set(), errors: [] });
		setBlockedByMap({});
		setMaintenanceInfoMap({});
		const startEpoch = cdtToEpoch(startTime, eventTimeZone);
		const endEpoch = cdtToEpoch(finishTime, eventTimeZone);
		// Skip until we have the minimum the backend needs: org +
		// timeframe. Without those the response is meaningless. We do NOT
		// gate on resource/pilot/SIC/instructor selections — the precheck
		// must run to populate `candidates.*` for the response-driven
		// dropdowns, otherwise Create-mode chickens-and-eggs (dropdowns
		// need candidates, candidates need precheck, precheck cannot
		// demand a selection that has nowhere to come from).
		if (!formOrgUnique || !startEpoch || !endEpoch) return undefined;

		const reqId = ++validateReqIdRef.current;
		// HW-1081: the backend answers "This organization does not have such pilot"
		// (OrganizationPilotNotFoundException, a 404 with no candidate lists) when the
		// Pilot is not a pilot of this org. If the form filled that Pilot in by itself,
		// clear it: the pilotId change re-runs this pre-check, which then returns the
		// org's real pilot list. A Pilot the user picked is left alone.
		const PILOT_NOT_FOUND = /does not have such pilot/i;
		const dropRejectedAutoPilot = errors => {
			const autoId = autoPilotIdRef.current;
			if (!autoId || String(pilotId) !== autoId || !errors.some(e => PILOT_NOT_FOUND.test(e))) {
				return { errors, pilotDropped: false };
			}
			autoPilotIdRef.current = '';
			setPilotId('');
			setCandidates(prev => ({
				...prev,
				pilots: mapValues(prev.pilots).filter(p => String(p.id) !== autoId)
			}));
			return { errors: errors.filter(e => !PILOT_NOT_FOUND.test(e)), pilotDropped: true };
		};
		const fire = async () => {
			// Use the shared payload builder so pre-check and final submit
			// stay in lock-step. Mode is parent-aware:
			//   - Create context → 'precheck' (emits `recurringAction='add'`).
			//   - Edit context   → 'edit'    (emits `recurringAction='save'`
			//     plus `eventsForAction` when the form is recurring).
			// On the edit route the backend keys off the existing event id in
			// the URL plus the save-shape payload to skip-self in conflict
			// detection. Sending 'add' here makes it treat the existing series
			// as a new one and report its own future instances as conflicts.
			// (See JSDoc on `buildEventPayload`.)
			const payload = buildEventPayload(
				{
					formOrgUnique,
					eventType,
					startTime,
					finishTime,
					locationId,
					pilotId,
					sicPilotId,
					instructorId,
					activityType,
					eventSharing,
					resourceIds,
					comments,
					recurring,
					recurringType,
					daysOfWeek,
					repsType,
					repsInterval,
					reps,
					recurringEndDate,
					customValues
				},
				{
					mode: isEditMode ? 'edit' : 'precheck',
					timezone,
					eventLocationTz: eventTimeZone,
					canManageRecurrent,
					customFields,
					// Waiting-list flow must pre-check with `addToWaiting: true`,
					// else the busy resource comes back as a hard conflict. Uses
					// the effective flag (seed → reactive signal), not the raw
					// create-modal prop, so edit-flow prechecks send it too.
					forceAddToWaiting: effectiveAddToWaiting
				}
			);
			// Pre-check expects only the cross-cutting fields — drop the
			// customFields block to keep the legacy debounce shape.
			delete payload.customFields;
			try {
				// Edit mode passes `eventId` so backend can skip-self in
				// conflict detection.
				const res = await CalendarApi.validateEventParams(
					payload,
					isEditMode ? eventId : undefined,
					formOrgUnique
				);
				if (validateReqIdRef.current !== reqId) return; // stale
				const body = res?.data ?? res;
				const data = body?.data ?? body ?? {};
				// Error lines via the shared extractor (see extractPrecheckErrors).
				const { errors: allErrors, pilotDropped } = dropRejectedAutoPilot(
					extractPrecheckErrors(body)
				);
				// List each blocker once: a line the aircraft already carries in its own
				// `blockedBy` stays out of the callout (it still blocks submit).
				const errors = dropResourceBlockerTwins(allErrors, data?.resources);
				const usedIds = (arr, idKey) => {
					const out = new Set();
					(Array.isArray(arr) ? arr : []).forEach(item => {
						if (!item || !item.used) return;
						const id = item[idKey] ?? item.id ?? item.userID ?? item.resourceID;
						if (id != null) out.add(String(id));
					});
					return out;
				};
				setConflicts({
					resources: usedIds(data?.resources, 'resourceID'),
					errors,
					instructorsBlocker: extractInstructorsBlocker(body)
				});
				// Split into a HARD-blocker map (submit gate — `blockedBy` only) and a
				// display-only maintenance info map. See `buildResourceBlockerMaps` for
				// why `maintenance` must never contribute to the submit gate.
				const { blockedByMap: newBlockedByMap, maintenanceInfoMap: newMaintenanceInfoMap } =
					buildResourceBlockerMaps(data?.resources);
				setBlockedByMap(newBlockedByMap);
				setMaintenanceInfoMap(newMaintenanceInfoMap);
				// Bidirectional waiting-list flip (day-view parity,
				// `calendar-day.actions.js:364-372`). Scan the selected resources
				// in this response: if any carries a truthy `addToWaiting`, the
				// slot is busy → flip on; if none, the slot is free → flip off
				// (enables demotion). Symfony serializes the flag as boolean
				// `true`, so the scan accepts `true | 1 | '1'` — never `parseInt`.
				// `precheckSettled` hands control from the reserve seed to this
				// signal once the first response lands.
				setLatestPrecheckSignal(scanResourcesAddToWaiting(data?.resources, resourceIds));
				setPrecheckSettled(true);
				// Pilot / SIC / Instructor dropdowns are response-driven.
				// Backend uses `coPilots`; V2 internal name stays `sics`.
				// Instructor items are `EventParamsResourceDto` (id = resource
				// id, plus `instructorId` = user id) so `item.id` aligns with
				// the existing `instructorId` form-state value.
				// Merge the response over the seeded assigned candidates: response
				// items win (they carry `used` / instructor `instructorId`), but the
				// currently-assigned pilot/SIC/instructor is preserved (deduped by
				// id) if the response omits it — so a checked-out event's assigned
				// people don't vanish from their fields (Task:20 Symptom B).
				setCandidates(prev => ({
					pilots: mergeKeepAssigned(data?.users?.pilots, prev.pilots, pilotDropped ? '' : pilotId),
					sics: mergeKeepAssigned(data?.users?.coPilots, prev.sics, sicPilotId),
					instructors: mergeKeepAssigned(data?.users?.instructors, prev.instructors, instructorId)
				}));
				const first = errors[0];
				// Never toast a pre-check that lands AFTER the modal closed (the
				// tester's "box went away, then the validation error appeared").
				if (first && openRef.current && first !== lastToastedErrorRef.current) {
					lastToastedErrorRef.current = first;
					addToast({ title: first, color: 'warning' });
				}
				if (!first) lastToastedErrorRef.current = '';
			} catch (err) {
				if (validateReqIdRef.current !== reqId) return;
				// An HTTP 4xx carries real validation errors in the body (apiRequest throws
				// on !response.ok with the parsed body in err.response.data) — surface them
				// exactly like 200-path conflicts, e.g. "End date of the sequence must be
				// after the date of the first event". Only true network failures (no
				// err.response) stay silent — submit will surface those.
				const { errors: serverErrors } = dropRejectedAutoPilot(
					Array.isArray(err?.response?.data?.error)
						? err.response.data.error.filter(e => typeof e === 'string' && e.trim())
						: []
				);
				if (serverErrors.length) {
					setConflicts({ resources: new Set(), errors: serverErrors });
					const first = serverErrors[0];
					if (first !== lastToastedErrorRef.current) {
						lastToastedErrorRef.current = first;
						addToast({ title: first, color: 'warning' });
					}
				}
			}
		};
		// First eligible run after open: fire immediately so Edit-mode
		// dropdowns are marked before the user touches any field.
		// Subsequent runs (driven by user edits) stay debounced.
		if (!firstPrecheckDoneRef.current) {
			firstPrecheckDoneRef.current = true;
			fire();
			return undefined;
		}
		const handle = setTimeout(fire, 600);
		return () => clearTimeout(handle);
	}, [
		isOpen,
		formOrgUnique,
		locationId,
		pilotId,
		sicPilotId,
		instructorId,
		resourceIds,
		startTime,
		finishTime,
		eventType,
		comments,
		timezone,
		// Recurrence state — re-run pre-check whenever the user toggles
		// recurrence on/off or changes any field that alters the generated
		// series (so backend reports per-occurrence conflicts inline).
		recurring,
		recurringType,
		repsInterval,
		repsType,
		reps,
		recurringEndDate,
		daysOfWeek,
		canManageRecurrent,
		// Edit-context precheck emits `recurringAction='save'` instead of
		// `'add'`. Re-run when the parent mode flips so payload mode stays
		// correct across modal reuse.
		isEditMode,
		// Effective waiting-list flag (subsumes `forceAddToWaiting`). Re-runs the
		// precheck only when the flag genuinely transitions (promotion/demotion),
		// which converges in one extra call — the response reasserts the same
		// value, so no further re-render fires. Using the derived value (not the
		// raw seed/signal) avoids an extra precheck when the flip is a no-op.
		effectiveAddToWaiting
	]);

	const handleSubmit = async () => {
		// Required-field validation. Per dev DB config: org/pilot/location/
		// start/end/resource/comments are required for regular events;
		// maintenance drops the pilot requirement (Pilot field is hidden).
		const fail = msg => addToast({ title: msg, color: 'warning' });
		if (!formOrgUnique) return fail('Organization is required');
		if (!locationId) return fail('Location is required');
		if (eventType !== 'maintenance' && !pilotId && !pilotIsOptional)
			return fail('Pilot is required');
		if (resourceIds.length === 0 && (isMaintenance || !instructorId))
			return fail('Resource is required');
		if (enforseComments && (!comments || !comments.trim())) return fail('Comments are required');
		const startEpoch = cdtToEpoch(startTime, eventTimeZone);
		const endEpoch = cdtToEpoch(finishTime, eventTimeZone);
		if (!startEpoch || !endEpoch) return fail('Invalid date/time');
		if (endEpoch <= startEpoch) return fail('End time must be after start time');
		// Custom field required check. Date type stores a CalendarDate object,
		// numeric/text store strings — handle both shapes. Skipped for
		// maintenance events (custom fields aren't shown in that mode).
		if (eventType !== 'maintenance') {
			for (const cf of customFields) {
				if (!cf.required) continue;
				const v = customValues[cf.id];
				const empty =
					v == null ||
					(typeof v === 'string' && !v.trim()) ||
					(typeof v === 'object' && cf.type === 'date' && v.year == null);
				if (empty) return fail(`${cf.name} is required`);
			}
		}
		const isMaintenancePayload = eventType === 'maintenance';
		// `willRecur` AND-gates on `canManageRecurrent` so a user without the
		// `calendarManageRecurrentEvent` permission cannot accidentally (or via
		// state-leak) submit a recurring event — even if `recurring` happens
		// to be true in local state, payload force-falls back to neutral
		// fields. Mirrors the UI hide-not-disable rule from R11.3.
		const willRecur = !isMaintenancePayload && recurring && canManageRecurrent;
		// Recurrence validation — only the active `repsType` mode is checked
		// (preserved value of the inactive mode is not validated).
		if (willRecur) {
			const intervalNum = Number(repsInterval);
			if (!Number.isFinite(intervalNum) || intervalNum < 1 || intervalNum > 30) {
				return fail('Repeat every must be between 1 and 30');
			}
			if (recurringType === 'weeks' && daysOfWeek.length === 0) {
				return fail('Select at least one day of week');
			}
			if (repsType === 'number') {
				const repsNum = Number(reps);
				if (!Number.isFinite(repsNum) || repsNum < 1) {
					return fail('Number of repetitions must be at least 1');
				}
			} else if (repsType === 'date') {
				if (!recurringEndDate) return fail('Recurrence end date is required');
				// CalendarDate.compare is tz-independent — avoids browser-tz
				// getFullYear/getMonth/getDate reads that break near midnight.
				const startCdt = epochToCalendarDateTime(startEpoch, eventTimeZone);
				const startCalendarDate = new CalendarDate(startCdt.year, startCdt.month, startCdt.day);
				if (recurringEndDate.compare(startCalendarDate) <= 0) {
					return fail('Recurrence end date must be after event start date');
				}
			}
		}
		// Hard-blocker gate — precheck async result. Gate AFTER required-field
		// checks so the user sees the most actionable error first (missing field
		// beats blocked resource). Soft warnings (medical cert, busy) don't
		// populate `blockedByMap` and still allow submit.
		if (hasHardBlocker) {
			const firstBlockedId = resourceIds.find(id => (blockedByMap[String(id)] || []).length > 0);
			const resourceLabel = findLabel(resources, firstBlockedId) || 'Resource';
			const blockerMsg = (blockedByMap[String(firstBlockedId)] || [])[0] || 'Blocked';
			return fail(`${resourceLabel}: ${blockerMsg}`);
		}
		setSubmitting(true);
		// ── FINAL VALIDATION GATE (legacy parity; tester report 2026-07-15) ──
		// The box must NOT close until all validation checks complete. The
		// debounced live pre-check can still be in flight (or not yet fired
		// for the latest edits) when the user hits submit — previously the
		// create raced it: the event saved, the modal closed, and the
		// pre-check's "Pilot's Medical Certificate does not meet the aircraft
		// requirements" arrived as a toast AFTER the box was gone. Run one
		// awaited final check and gate on it. Only the booked-conflict family
		// passes through (it legitimately becomes a BACKUP reservation —
		// HW-1026 flow); anything else keeps the box open with the errors in
		// the inline conflicts callout, matching the legacy save gate.
		if (eventType !== 'maintenance') {
			try {
				const precheckPayload = buildEventPayload(
					{
						formOrgUnique,
						eventType,
						startTime,
						finishTime,
						locationId,
						pilotId,
						sicPilotId,
						instructorId,
						activityType,
						eventSharing,
						resourceIds,
						comments,
						recurring,
						recurringType,
						daysOfWeek,
						repsType,
						repsInterval,
						reps,
						recurringEndDate,
						customValues
					},
					{
						mode: isEditMode ? 'edit' : 'precheck',
						timezone,
						eventLocationTz: eventTimeZone,
						canManageRecurrent,
						customFields,
						forceAddToWaiting: effectiveAddToWaiting
					}
				);
				delete precheckPayload.customFields;
				const res = await CalendarApi.validateEventParams(
					precheckPayload,
					isEditMode ? eventId : undefined,
					formOrgUnique
				);
				const finalBody = res?.data ?? res;
				const finalErrors = extractPrecheckErrors(finalBody);
				const blocking = blockingPrecheckErrors(finalErrors);
				if (blocking.length > 0) {
					// Surface in the existing inline conflicts callout + keep open.
					setConflicts(prev => ({
						...prev,
						errors: dropResourceBlockerTwins(
							finalErrors,
							(finalBody?.data ?? finalBody)?.resources
						),
						instructorsBlocker: extractInstructorsBlocker(finalBody)
					}));
					addToast({ title: blocking[0], color: 'warning' });
					setSubmitting(false);
					return;
				}
			} catch (precheckErr) {
				// Transport failure of the final check must not dead-end the form —
				// the create call itself remains the authority on hard errors.
			}
		}
		// Build the wire payload via the shared helper — single source of
		// truth shared with the pre-check (above). Edit mode emits
		// `recurringAction='save'`; create / precheck emit `'add'`. The
		// caller (`onSave`) decides the final endpoint and any recurring
		// scope dialog (which it must run before invoking this submit when
		// editing a recurring event with permission).
		const payload = buildEventPayload(
			{
				formOrgUnique,
				eventType,
				startTime,
				finishTime,
				locationId,
				pilotId,
				sicPilotId,
				instructorId,
				activityType,
				eventSharing,
				resourceIds,
				comments,
				recurring,
				recurringType,
				daysOfWeek,
				repsType,
				repsInterval,
				reps,
				recurringEndDate,
				customValues
			},
			{
				mode,
				timezone,
				eventLocationTz: eventTimeZone,
				canManageRecurrent,
				customFields,
				// Effective flag: forced (create modal) → reactive signal → seed.
				// This is what makes an edited waiting-list event save as waiting.
				forceAddToWaiting: effectiveAddToWaiting
			}
		);
		try {
			// Inline scope short-circuits the separate `RecurringScopeConfirm` modal:
			// passed only when editing an already-recurring event with permission.
			// Defaults to 'following' (legacy parity); user can pick Current/Following/All
			// from the inline dropdown in the recurring section.
			const wasRecurring = !!initialFormState?.recurring;
			const inlineScope =
				isEditMode && wasRecurring && canManageRecurrent ? recurringScope : undefined;
			const result = await onSave?.(payload, formOrgUnique, inlineScope);
			// Caller may return `{ ok: false }` to keep the form open with
			// submitting state cleared (e.g. backend validation error already
			// surfaced via toast). Anything else is treated as success — the
			// caller will close the modal itself.
			if (result && result.ok === false) {
				setSubmitting(false);
				return;
			}
			setSubmitting(false);
		} catch (err) {
			const msg =
				err?.response?.data?.error?.[0] ||
				err?.message ||
				(isEditMode ? 'Save failed' : 'Create failed');
			addToast({
				title: isEditMode ? 'Save failed' : 'Create failed',
				description: msg,
				color: 'danger'
			});
			setSubmitting(false);
		}
	};

	// HW-1026/1027 — PRE-SUBMIT backup clarity. Legacy's server-rendered form
	// told the user BEFORE submitting that the slot was taken; here the live
	// debounced pre-check knows the same thing, so surface the CONSEQUENCE:
	// a resource "already booked" conflict becomes a BACKUP (waiting-list)
	// reservation server-side. Pilot/SIC/instructor "engaged" conflicts are
	// genuine rejections and keep reading as plain conflict lines.
	const willBeBackup =
		!isEditMode &&
		!forceAddToWaiting &&
		!isMaintenance &&
		(resourceIds.some(id => conflicts.resources.has(String(id))) ||
			conflicts.errors.some(e => /already booked/i.test(String(e))));

	return (
		<>
			<ModalBody className="pt-5">
				{willBeBackup && (
					<div
						role="alert"
						aria-live="polite"
						className="mb-3 rounded-medium border border-warning-300 bg-warning-100 px-3 py-2"
					>
						<p className="m-0 text-sm font-semibold text-warning-800">
							This will be a BACKUP reservation
						</p>
						<p className="m-0 mt-0.5 text-xs text-warning-700">
							The selected aircraft is already booked for this time, so this event will go on the
							waiting list — shown striped on the calendar — instead of a confirmed slot.
						</p>
					</div>
				)}
				{conflicts.errors.length > 0 && (
					<div
						role="alert"
						aria-live="polite"
						className="mb-3 space-y-1 rounded-medium border border-warning-200 bg-warning-50 px-3 py-2"
					>
						{conflicts.errors.map((e, idx) => (
							<p key={`${idx}-${e}`} className="m-0 text-sm text-warning-700">
								{e}
							</p>
						))}
					</div>
				)}
				{!isEditMode && canManageMaintenance && (
					<div className="mb-4">
						<Tabs
							aria-label="Event Type"
							selectedKey={eventType}
							onSelectionChange={key => {
								const t = String(key);
								setEventType(t);
								onEventTypeChange?.(t);
								// Reset regular-only fields on a regular↔maintenance
								// switch — maintenance events never carry an Activity
								// Type or Event Sharing flag (day-view parity).
								setActivityType('');
								setEventSharing(false);
							}}
							size="sm"
							color="primary"
							variant="light"
							classNames={{
								// Global twig override forces selected-tab bg to
								// white (`react-heroui-layout.twig:528`), so the
								// default white-on-primary text disappears. Use
								// primary text on white instead.
								tabList: 'bg-default-100 p-1 rounded-medium gap-1',
								tabContent: 'group-data-[selected=true]:text-primary font-medium'
							}}
						>
							<Tab
								key="air"
								title={
									<span className="flex items-center gap-1.5">
										<Plane className="w-3.5 h-3.5" /> Regular
									</span>
								}
							/>
							<Tab
								key="maintenance"
								title={
									<span className="flex items-center gap-1.5">
										<Wrench className="w-3.5 h-3.5" /> Maintenance
									</span>
								}
							/>
						</Tabs>
					</div>
				)}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0">
					{/* Left column — mirror EventViewModal order (Status omitted
					   because new event has no status yet) */}
					<div>
						<Field
							label="Organization"
							required
							isInvalid={orgInvalid}
							hint={isOrgOverdue ? 'The organization is overdue' : null}
						>
							{/* Rem30 Item 4: the organization is locked to the
								   dashboard org. With the org filter removed there is
								   no cross-org choice to make, so this is a static
								   read-only display — no dropdown, in both create and
								   edit mode (edit was already disabled). `formOrgUnique`
								   (seeded from the `orgUnique` prop) is untouched and
								   still drives locations, resources, pilots/instructors,
								   custom fields, default length, the conflict precheck
								   and save-endpoint routing. */}
							<span aria-label="Organization" className="block py-1">
								{formOrgName || '—'}
							</span>
						</Field>
						<Field label="Location" required isInvalid={locationInvalid}>
							<Autocomplete
								aria-label="Location"
								size="sm"
								isInvalid={locationInvalid}
								placeholder="Search location…"
								defaultItems={locations}
								selectedKey={locationId || null}
								onSelectionChange={k => {
									const next = k ? String(k) : '';
									const nextLoc = locationsMap?.[next];
									const newTz = nextLoc?.timeZoneId || timezone || 'UTC';
									const oldTz = eventTimeZone;
									if (newTz !== oldTz) {
										if (startTime) {
											const e = cdtToEpoch(startTime, oldTz);
											setStartTime(epochToCalendarDateTime(e, newTz));
										}
										if (finishTime) {
											const e = cdtToEpoch(finishTime, oldTz);
											setFinishTime(epochToCalendarDateTime(e, newTz));
										}
									}
									setLocationId(next);
									// Resources cascade off location — clear the
									// previously picked resource so we don't carry
									// over an aircraft from the prior location.
									setResourceIds([]);
								}}
							>
								{item => {
									const loc = locationsMap?.[item.id];
									const tz = loc?.timeZoneId || '';
									return (
										<AutocompleteItem
											key={item.id}
											textValue={formatLocationLabelString(item.label, tz)}
										>
											<LocationLabel name={item.label} timeZoneId={tz} />
										</AutocompleteItem>
									);
								}}
							</Autocomplete>
						</Field>
						{!isMaintenance && (
							<>
								<Field
									label="Pilot"
									required={!pilotIsOptional}
									isInvalid={pilotInvalid}
									hint={pilotHint}
								>
									<Autocomplete
										aria-label="Pilot"
										size="sm"
										isInvalid={pilotInvalid}
										placeholder="Search pilot…"
										defaultItems={candidates.pilots}
										selectedKey={pilotId || null}
										onSelectionChange={k => {
											autoPilotIdRef.current = '';
											setPilotId(k ? String(k) : '');
										}}
										disabledKeys={[sicPilotId, instructorUserId].filter(Boolean)}
									>
										{renderItem}
									</Autocomplete>
								</Field>
								<Field label="SIC Pilot" hint={sicHint}>
									<Autocomplete
										aria-label="SIC Pilot"
										size="sm"
										placeholder="Search SIC…"
										defaultItems={candidates.sics}
										selectedKey={sicPilotId || null}
										onSelectionChange={k => setSicPilotId(k ? String(k) : '')}
										disabledKeys={[pilotId, instructorUserId].filter(Boolean)}
									>
										{renderItem}
									</Autocomplete>
								</Field>
								<Field label="Instructor" isInvalid={instructorHardBlocker} hint={instructorHint}>
									<Autocomplete
										aria-label="Instructor"
										size="sm"
										isInvalid={instructorHardBlocker}
										placeholder="Search instructor…"
										defaultItems={candidates.instructors}
										selectedKey={instructorId || null}
										onSelectionChange={k => setInstructorId(k ? String(k) : '')}
										disabledKeys={instructorDisabledKeys}
									>
										{renderItem}
									</Autocomplete>
								</Field>
								{/* Rem32 Task 1 — Activity Type (day-view parity,
									   `calendar.event-add.js:738-753`). Optional, regular
									   events only (this fragment is `!isMaintenance`-gated);
									   hidden entirely when the org has no active types.
									   Clearing the Autocomplete sets "Not set" (''). */}
								{activeActivityTypeOptions.length > 0 && (
									<Field label="Activity Type">
										<Autocomplete
											aria-label="Activity Type"
											size="sm"
											placeholder="Not set"
											defaultItems={activeActivityTypeOptions}
											selectedKey={activityType || null}
											onSelectionChange={k => setActivityType(k ? String(k) : '')}
										>
											{item => <AutocompleteItem key={item.value}>{item.label}</AutocompleteItem>}
										</Autocomplete>
										{activityType && activityTypesMap[activityType]?.color && (
											<div
												className="h-2 w-full rounded-sm mt-1.5"
												style={{ backgroundColor: activityTypesMap[activityType].color }}
											/>
										)}
									</Field>
								)}
							</>
						)}
					</div>

					{/* Right column — mirror EventViewModal order */}
					<div>
						<Field label="Start Time" required isInvalid={startTimeInvalid}>
							<AppDatePicker
								aria-label="Start Time"
								granularity="minute"
								hourCycle="h23"
								hideTimeZone
								value={startTime}
								onChange={cdt => {
									setStartTime(cdt);
									// Preserve the user's chosen duration (day-view parity):
									// shift end by the same delta as start. Fall back to
									// eventDefaultLength only when no valid end is set yet.
									if (cdt) {
										const newStartMs = cdtToJSDate(cdt, eventTimeZone)?.getTime();
										if (Number.isFinite(newStartMs)) {
											const prevStartMs = startTime
												? cdtToJSDate(startTime, eventTimeZone)?.getTime()
												: null;
											const prevFinishMs = finishTime
												? cdtToJSDate(finishTime, eventTimeZone)?.getTime()
												: null;
											const durationMs =
												prevStartMs != null && prevFinishMs != null && prevFinishMs > prevStartMs
													? prevFinishMs - prevStartMs
													: eventDefaultLengthSec * 1000;
											setFinishTime(fromDate(new Date(newStartMs + durationMs), eventTimeZone));
										}
									}
								}}
							/>
						</Field>
						<Field label="End Time" required isInvalid={endTimeInvalid}>
							<AppDatePicker
								aria-label="End Time"
								granularity="minute"
								hourCycle="h23"
								hideTimeZone
								value={finishTime}
								onChange={setFinishTime}
							/>
						</Field>
						<Field
							label={isMaintenance ? 'Add Resource' : 'Resources'}
							required={isMaintenance || !instructorId}
							isInvalid={resourceInvalid}
							hint={resourceHint}
						>
							{/* Tag-input pattern: search + add via Autocomplete,
								    selected items show as removable chips above. Once
								    backend ships `typeName` on FilteredResourceDto, the
								    `AutocompleteSection` grouping kicks in automatically. */}
							{resourceIds.length > 0 && (
								<div className="flex flex-wrap items-center gap-1 mb-1.5">
									{resourceIds.map(id => {
										const label = findLabel(resources, id) || id;
										const used = conflicts.resources.has(String(id));
										return (
											<Chip
												key={id}
												size="sm"
												variant="flat"
												color={used ? 'danger' : 'default'}
												onClose={() =>
													setResourceIds(prev => prev.filter(x => String(x) !== String(id)))
												}
											>
												{onResourceClick ? (
													<button
														type="button"
														className="cursor-pointer hover:opacity-70 transition-opacity"
														onClick={() => onResourceClick(id, label)}
													>
														{used ? `${label} · Busy` : label}
													</button>
												) : used ? (
													`${label} · Busy`
												) : (
													label
												)}
											</Chip>
										);
									})}
									{resourceIds.length > 1 && (
										<button
											type="button"
											className="text-[11px] text-default-500 hover:text-danger transition-colors ml-1"
											onClick={() => setResourceIds([])}
										>
											Clear all
										</button>
									)}
								</div>
							)}
							{resourceIds.some(id => (blockedByMap[String(id)] || []).length > 0) && (
								<div className="mb-1.5 flex flex-col gap-0.5">
									{resourceIds.map(id => {
										const blockers = blockedByMap[String(id)] || [];
										if (blockers.length === 0) return null;
										const label = findLabel(resources, id) || id;
										return (
											<p key={id} className="text-danger text-xs">
												<span className="font-medium">{label}:</span> {blockers.join(', ')}
											</p>
										);
									})}
								</div>
							)}
							{/* Maintenance is informational only — never gates submit (legacy
								    parity, `Calendar.php:2990-2997`). Rendered separately from
								    hard blockers above so it never reads as a save-blocking error. */}
							{resourceIds.some(id => (maintenanceInfoMap[String(id)] || []).length > 0) && (
								<div className="mb-1.5 flex flex-col gap-0.5">
									{resourceIds.map(id => {
										const info = maintenanceInfoMap[String(id)] || [];
										if (info.length === 0) return null;
										const label = findLabel(resources, id) || id;
										return (
											<p key={id} className="text-default-500 text-xs">
												<span className="font-medium">{label}:</span> {info.join(', ')}
											</p>
										);
									})}
								</div>
							)}
							{(!isMaintenance || resourceIds.length === 0) && (
								<Autocomplete
									// Key bump unmounts HeroUI's internal open
									// state after each selection so the dropdown
									// closes deterministically.
									key={resourcePickerKey}
									aria-label="Add resource"
									size="sm"
									placeholder={isMaintenance ? 'Select resource…' : 'Search and add resource…'}
									// `inputValue` is uncontrolled here so the field
									// re-clears naturally after each selection thanks
									// to `selectedKey={null}`. Parent state is the
									// chips array; the Autocomplete is just an adder.
									selectedKey={null}
									inputProps={{ ref: resourceInputRef }}
									// Rem24 Sub 8 — disable other aircraft once one
									// is selected (business rule: 1 aircraft per
									// event).
									disabledKeys={disabledAircraftKeys}
									onSelectionChange={key => {
										if (!key) return;
										const id = String(key);
										// Rem24 Sub 8 — block second aircraft pick.
										// `disabledKeys` already dims other aircraft
										// items, but defense-in-depth here in case
										// HeroUI lets a disabled item through
										// (keyboard race, programmatic select).
										if (
											aircraftResourceIds.has(id) &&
											selectedAircraftId &&
											String(selectedAircraftId) !== id
										) {
											addToast({
												title: 'Only one aircraft per event',
												description: 'Remove the current aircraft before adding another.',
												color: 'warning'
											});
											return;
										}
										setResourceIds(prev => {
											if (prev.includes(id)) return prev;
											// Maintenance caps to one.
											return isMaintenance ? [id] : [...prev, id];
										});
										// Force remount → HeroUI's internal open
										// state is wiped → dropdown closes.
										setResourcePickerKey(k => k + 1);
										// Defense-in-depth: also blur the input.
										resourceInputRef.current?.blur?.();
									}}
								>
									{resourceGroups.length === 1
										? resourceGroups[0].items
												.filter(it => !resourceIds.includes(String(it.id)))
												.map(item => {
													const used = conflicts.resources.has(String(item.id));
													const blockers = blockedByMap[String(item.id)] || [];
													// Display combines hard blockers + informational maintenance
													// text; only `blockers` (hard) gates submit elsewhere.
													const infoText = [
														...blockers,
														...(maintenanceInfoMap[String(item.id)] || [])
													];
													return (
														<AutocompleteItem key={item.id} textValue={item.label}>
															<div className="flex flex-col gap-0.5">
																<div className="flex items-center justify-between gap-2">
																	<span>{item.label}</span>
																	{blockers.length === 0 && used ? (
																		<Chip
																			size="sm"
																			color="danger"
																			variant="flat"
																			className="flex-shrink-0"
																		>
																			Busy
																		</Chip>
																	) : null}
																</div>
																{infoText.length > 0 ? (
																	<span className="text-default-400 text-xs">
																		{infoText.join(', ')}
																	</span>
																) : null}
															</div>
														</AutocompleteItem>
													);
												})
										: resourceGroups.map(group => (
												<AutocompleteSection key={group.key} title={group.title}>
													{group.items
														.filter(it => !resourceIds.includes(String(it.id)))
														.map(item => {
															const used = conflicts.resources.has(String(item.id));
															const blockers = blockedByMap[String(item.id)] || [];
															// Display combines hard blockers + informational maintenance
															// text; only `blockers` (hard) gates submit elsewhere.
															const infoText = [
																...blockers,
																...(maintenanceInfoMap[String(item.id)] || [])
															];
															return (
																<AutocompleteItem key={item.id} textValue={item.label}>
																	<div className="flex flex-col gap-0.5">
																		<div className="flex items-center justify-between gap-2">
																			<span>{item.label}</span>
																			{blockers.length === 0 && used ? (
																				<Chip
																					size="sm"
																					color="danger"
																					variant="flat"
																					className="flex-shrink-0"
																				>
																					Busy
																				</Chip>
																			) : null}
																		</div>
																		{infoText.length > 0 ? (
																			<span className="text-default-400 text-xs">
																				{infoText.join(', ')}
																			</span>
																		) : null}
																	</div>
																</AutocompleteItem>
															);
														})}
												</AutocompleteSection>
											))}
								</Autocomplete>
							)}
						</Field>
						<Field label="Comments" required={enforseComments} isInvalid={commentsInvalid}>
							<Textarea
								aria-label="Comments"
								isInvalid={commentsInvalid}
								value={comments}
								onValueChange={setComments}
								minRows={2}
							/>
						</Field>
						{/* Rem32 Task 2 — Event Sharing (day-view parity,
							   `calendar.event-add.js:1032-1047`). Shown only for regular
							   events when the org has event sharing enabled. */}
						{!isMaintenance && enableEventSharing && (
							<Field label="Event Sharing">
								<Checkbox size="sm" isSelected={eventSharing} onValueChange={setEventSharing}>
									Invite other pilots to participate in this event
								</Checkbox>
								<p className="text-xs text-default-500 mt-1">
									Users with <b>Pilot Role</b> will receive an email invitation to join in after you
									check off this box and save the event.
								</p>
							</Field>
						)}
						{!isMaintenance && canManageRecurrent && (
							<Field label="Recurring Event">
								<Checkbox isSelected={recurring} onValueChange={handleRecurringChange}>
									Recurring event
								</Checkbox>
								<AnimatePresence initial={false}>
									{recurring && (
										<motion.div
											key="recurrence-section"
											initial={reducedMotion ? false : { height: 0, opacity: 0 }}
											animate={{ height: 'auto', opacity: 1 }}
											exit={reducedMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
											transition={{ duration: 0.2, ease: 'easeOut' }}
											style={{ overflow: 'hidden' }}
										>
											<div className="mt-3 space-y-3">
												<div className="grid grid-cols-2 gap-2">
													<Input
														type="number"
														min={1}
														max={30}
														size="sm"
														label="Repeat every"
														aria-label="Repeat every"
														value={String(repsInterval)}
														onValueChange={v => {
															// Strip non-digits and clamp on commit; allow
															// empty during typing so the input doesn't
															// fight the user mid-edit.
															const digits = (v || '').replace(/\D/g, '');
															setRepsInterval(digits);
														}}
														onBlur={() => {
															const n = Number(repsInterval);
															if (!Number.isFinite(n) || n < 1) setRepsInterval('1');
															else if (n > 30) setRepsInterval('30');
														}}
													/>
													<Select
														size="sm"
														label="Period"
														aria-label="Period"
														selectedKeys={[recurringType]}
														onSelectionChange={keys => {
															const first = keys === 'all' ? null : Array.from(keys)[0];
															if (first) handleRecurringTypeChange(String(first));
														}}
													>
														<SelectItem key="day">Day</SelectItem>
														<SelectItem key="weeks">Week</SelectItem>
														<SelectItem key="month">Month</SelectItem>
													</Select>
												</div>

												{recurringType === 'weeks' && (
													<div>
														<p className="text-xs font-semibold text-default-500 uppercase tracking-wide mb-1">
															Repeat on
														</p>
														<div
															className="flex flex-wrap gap-1"
															role="group"
															aria-label="Days of week"
														>
															{WEEK_DAYS.map(({ key, short, full }) => {
																const selected = daysOfWeek.includes(key);
																const isLast = selected && daysOfWeek.length === 1;
																return (
																	<button
																		key={key}
																		type="button"
																		disabled={isLast}
																		aria-pressed={selected}
																		aria-label={full}
																		title={isLast ? 'At least one day must be selected' : full}
																		onClick={() => toggleDayOfWeek(key)}
																		className={`min-w-[36px] h-8 px-2 rounded-medium text-xs font-medium border transition-colors ${
																			selected
																				? 'bg-primary text-primary-foreground border-primary'
																				: 'bg-default-100 text-default-700 border-default-200 hover:bg-default-200'
																		} ${isLast ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
																	>
																		{short}
																	</button>
																);
															})}
														</div>
													</div>
												)}

												<div>
													<p className="text-xs font-semibold text-default-500 uppercase tracking-wide mb-1">
														Ends
													</p>
													<RadioGroup
														value={repsType}
														onValueChange={setRepsType}
														size="sm"
														classNames={{ wrapper: 'gap-2' }}
													>
														<div className="flex items-center gap-2">
															<Radio value="number">after</Radio>
															<Input
																type="number"
																min={1}
																size="sm"
																className="w-20"
																aria-label="Number of repetitions"
																isDisabled={repsType !== 'number'}
																value={String(reps)}
																onValueChange={v => {
																	const digits = (v || '').replace(/\D/g, '');
																	setReps(digits);
																}}
																onBlur={() => {
																	if (repsType !== 'number') return;
																	const n = Number(reps);
																	if (!Number.isFinite(n) || n < 1) setReps('1');
																}}
															/>
															<span className="text-sm text-default-500">times</span>
														</div>
														<div className="flex items-center gap-2">
															<Radio value="date">on</Radio>
															<div
																className={
																	repsType !== 'date' ? 'opacity-50 pointer-events-none' : ''
																}
															>
																<AppDatePicker
																	aria-label="Recurrence end date"
																	granularity="day"
																	hideTimeZone
																	isDisabled={repsType !== 'date'}
																	value={recurringEndDate}
																	onChange={setRecurringEndDate}
																/>
															</div>
														</div>
													</RadioGroup>
												</div>

												{summaryText && (
													<p className="text-xs text-default-600 italic">{summaryText}</p>
												)}

												{/* Inline Change Event scope — only when editing an
														already-recurring event with permission. Replaces the
														separate `RecurringScopeConfirm` modal for the Save flow;
														Delete still routes through the modal. */}
												{isEditMode && initialFormState?.recurring && (
													<div>
														<Select
															size="sm"
															label="Change Event"
															aria-label="Change Event scope"
															selectedKeys={[recurringScope]}
															onSelectionChange={keys => {
																const first = keys === 'all' ? null : Array.from(keys)[0];
																if (first) setRecurringScope(String(first));
															}}
														>
															{EDIT_RECURRING_OPTIONS.map(o => (
																<SelectItem key={o.value}>{o.label}</SelectItem>
															))}
														</Select>
													</div>
												)}
											</div>
										</motion.div>
									)}
								</AnimatePresence>
							</Field>
						)}
					</div>
				</div>

				{/* Per-org custom fields. 2-col grid mirrors the main body so
					    short fields (numeric / date) sit side-by-side instead of
					    each on its own row. Empty when the org has no fields.
					    Maintenance events skip them — they're flight-only data. */}
				{!isMaintenance && customFields.length > 0 && (
					<div className="mt-4 pt-4 border-t border-default-200 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0">
						{customFields.map(cf => {
							const cfVal = customValues[cf.id];
							const cfInvalid =
								!!cf.required &&
								(cfVal == null ||
									(typeof cfVal === 'string' && !cfVal.trim()) ||
									(typeof cfVal === 'object' && cf.type === 'date' && cfVal?.year == null));
							return (
								<div key={cf.id} className={cf.type === 'text' ? 'md:col-span-2' : ''}>
									<Field label={cf.name} required={cf.required} isInvalid={cfInvalid}>
										{cf.type === 'numeric' && (
											<Input
												type="number"
												size="sm"
												isInvalid={cfInvalid}
												aria-label={cf.name}
												value={customValues[cf.id] ?? ''}
												onValueChange={v => setCustomValue(cf.id, v)}
											/>
										)}
										{cf.type === 'text' && (
											<Textarea
												aria-label={cf.name}
												isInvalid={cfInvalid}
												value={customValues[cf.id] ?? ''}
												onValueChange={v => setCustomValue(cf.id, v)}
												minRows={2}
											/>
										)}
										{cf.type === 'date' && (
											<AppDatePicker
												aria-label={cf.name}
												granularity="day"
												hideTimeZone
												value={customValues[cf.id] ?? null}
												onChange={v => setCustomValue(cf.id, v)}
											/>
										)}
									</Field>
								</div>
							);
						})}
					</div>
				)}
			</ModalBody>
			<ModalFooter className="justify-end gap-2 border-t border-default-200 pt-3">
				<Button size="sm" variant="bordered" onPress={onClose}>
					Cancel
				</Button>
				<Button
					size="sm"
					color={willBeBackup ? 'warning' : 'primary'}
					isLoading={submitting}
					isDisabled={isFormInvalid}
					onPress={handleSubmit}
				>
					{resolveSubmitLabel({
						submitLabel,
						forceAddToWaiting,
						isEditMode,
						isMaintenance,
						willBeBackup
					})}
				</Button>
			</ModalFooter>
		</>
	);
}

export default EventFormBody;
